import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";

import { Button, Input, Popup, Radio } from "semantic-ui-react";

import moment, { Moment } from "moment";

import { HOLIDAY_TAG, parseMonthTimeENAndTH } from "./sequence/SetupWorkSchedule";

type PatternType = "none" | "onlydate" | "tillEndMonth" | "tillEndYear";

export type HolidayResult = {
  action: "ADD" | "DEL";
  date: Moment;
  detail: string;
  pattern: PatternType;
  targetDates: Moment[];
};

export type HolidaysType = {
  allDay?: boolean;
  end: Moment;
  resource?: any;
  start: Moment;
  title: string;
};

type HolidayPopupProps = {
  id?: any;
  dayOfWeekText: string;
  holidays?: HolidaysType[];
  open: boolean;
  position?: "bottom center" | "left center" | "right center" | "top center";
  selectedDate: Moment | null;
  trigger: React.ReactNode;
  onClose: () => void;
  onConfirm: (result: HolidayResult) => void;
};

const findTargetHolidays = (date: Moment, pattern: PatternType): Moment[] => {
  switch (pattern) {
    case "none": {
      return [];
    }
    case "onlydate": {
      return [date.clone()];
    }
    case "tillEndMonth": {
      const targets: Moment[] = [];
      const endOfMonth = moment(date).endOf("month");
      const current = moment(date);

      while (current.isSameOrBefore(endOfMonth, "day")) {
        targets.push(current.clone());
        current.add(7, "days");
      }

      return targets;
    }
    case "tillEndYear": {
      const targets: Moment[] = [];
      const endOfYear = moment(date).endOf("year");
      const current = moment(date);

      while (current.isSameOrBefore(endOfYear, "day")) {
        targets.push(current.clone());
        current.add(7, "days");
      }

      return targets;
    }

    default: {
      return [];
    }
  }
};

const HolidayPopup: React.FC<HolidayPopupProps> = (props) => {
  const [pattern, setPattern] = useState<PatternType>("none");
  const [detail, setDetail] = useState<string>("");
  const [targetDates, setTargetDates] = useState<Moment[]>([]);

  // ตรวจสอบว่าวันที่เลือกเป็นวันหยุดอยู่แล้วหรือไม่
  const isHoliday = (props.holidays ?? []).some(
    (holiday) => holiday.title === HOLIDAY_TAG && holiday.start.isSame(props.selectedDate, "day")
  );

  // คำนวณ target dates ทุกครั้งที่ pattern หรือ selectedDate เปลี่ยน
  useEffect(() => {
    if (props.selectedDate && pattern !== "none") {
      const targets = findTargetHolidays(props.selectedDate, pattern);

      setTargetDates(targets);
    } else {
      setTargetDates([]);
    }
  }, [pattern, props.selectedDate]);

  // Reset state เมื่อปิด popup หรือโหลดข้อมูลเดิมเมื่อเปิด popup
  useEffect(() => {
    if (!props.open) {
      setPattern("none");
      setDetail("");
      setTargetDates([]);

      return;
    }

    setPattern("onlydate");

    // เมื่อเปิด popup - ดึงข้อมูล detail ที่เคยบันทึกไว้
    if (props.selectedDate && isHoliday) {
      const existingHoliday = (props.holidays ?? []).find(
        (holiday) =>
          holiday.title === HOLIDAY_TAG && holiday.start.isSame(props.selectedDate, "day")
      );

      if (existingHoliday?.resource) {
        setDetail(existingHoliday.resource);
      }
    }
  }, [isHoliday, props.holidays, props.open, props.selectedDate]);

  const buttonId = useMemo(() => {
    const buttonType = isHoliday ? "Delete" : "Add";

    return props.id ? `${props.id}-Button-${buttonType}` : `HolidayPopup-Button-${buttonType}`;
  }, [isHoliday, props.id]);

  const handlePatternChange = (selectedPattern: PatternType) => {
    setPattern(selectedPattern);
  };

  const handleConfirm = () => {
    if (!props.selectedDate) {
      return;
    }

    const result: HolidayResult = {
      action: isHoliday ? "DEL" : "ADD",
      date: props.selectedDate,
      detail,
      pattern,
      targetDates,
    };

    props.onConfirm(result);
    props.onClose();
  };

  return (
    <Popup
      id={props.id ? `${props.id}-Popup` : "table-time-shift"}
      open={props.open}
      position={props.position || "bottom center"}
      trigger={props.trigger}
      on="click"
      onClick={(e: SyntheticEvent) => {
        e.stopPropagation();
      }}
      onClose={props.onClose}
      content={
        <div className="popup-holiday">
          <Radio
            id={props.id ? `${props.id}-Radio-Onlydate` : "HolidayPopup-Radio-Onlydate"}
            checked={pattern === "onlydate"}
            name="radioGroup"
            value="onlydate"
            toggle
            onChange={(e, { value }) => {
              handlePatternChange(value as PatternType);
            }}
            label={
              isHoliday
                ? `ยกเลิกวันหยุดเฉพาะวัน${props.dayOfWeekText}นี้`
                : `เลือกวันหยุดเฉพาะวัน${props.dayOfWeekText}นี้`
            }
          />

          <Radio
            id={props.id ? `${props.id}-Radio-TillEndMonth` : "HolidayPopup-Radio-TillEndMonth"}
            checked={pattern === "tillEndMonth"}
            name="radioGroup"
            size="tiny"
            value="tillEndMonth"
            toggle
            onChange={(e, { value }) => {
              handlePatternChange(value as PatternType);
            }}
            label={
              isHoliday
                ? `ยกเลิกวันหยุดทุกวัน${props.dayOfWeekText}ถึงสิ้นเดือน`
                : `เลือกวันหยุดทุกวัน${props.dayOfWeekText}ถึงสิ้นเดือน`
            }
          />

          <Input
            id={props.id ? `${props.id}-Input-Detail` : "HolidayPopup-Input-Detail"}
            style={{ fontSize: "14px" }}
            disabled={isHoliday}
            placeholder="รายละเอียดเพิ่มเติม"
            value={detail}
            fluid
            onChange={(e, data) => {
              setDetail(data.value);
            }}
          />

          <Button
            id={buttonId}
            style={{ fontWeight: "bold" }}
            color={isHoliday ? "red" : "green"}
            disabled={pattern === "none"}
            size="small"
            fluid
            onClick={handleConfirm}
          >
            {isHoliday ? "DELETE" : "ADD"}
          </Button>

          {targetDates.length > 0 && (
            <div
              style={{
                borderTop: "1px solid #e0e0e0",
                paddingTop: "8px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  color: isHoliday ? "#db2828" : "#21ba45",
                  fontSize: "14px",
                  fontWeight: "bold",
                  marginBottom: "8px",
                }}
              >
                {isHoliday ? "วันที่เตรียมลบ" : "วันที่เตรียมเพิ่ม"}
              </div>
              <div
                style={{
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #e9ecef",
                  borderRadius: "4px",
                  maxHeight: "200px",
                  overflowY: "auto",
                  padding: "8px",
                }}
              >
                {targetDates.map((date, index) => (
                  <div
                    key={`hint-${index}`}
                    style={{
                      borderBottom: index < targetDates.length - 1 ? "1px solid #dee2e6" : "none",
                      fontSize: "13px",
                      padding: "2px 0",
                    }}
                  >
                    {parseMonthTimeENAndTH(date)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      }
    />
  );
};

HolidayPopup.displayName = "HolidayPopup";

export default React.memo(HolidayPopup);
